'use client';


import { useMemo, useState } from 'react';
import { calculateFirstReflections, type FirstReflectionPoints } from '@/lib/acousticsPhysics';
import { resizeRoom, type RoomBounds } from '@/lib/tools/roomLayout';
import type { LengthUnit } from '@/lib/tools/units';
import { formatLength } from '@/lib/tools/units';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import ToolMetricCard from '@/components/tools/ToolMetricCard';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import RoomPlan2D from '@/components/tools/RoomPlan2D';

const PLAN_BOUNDS: RoomBounds = {
  length: { min: 3, max: 10 },
  width: { min: 2.5, max: 8 },
};

interface WidgetProps {
  initialReflections: FirstReflectionPoints;
}

export default function FirstReflectionWidget(_unused: WidgetProps) {
  void _unused;
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(5.0);
  const [widthM, setWidthM] = useState(4.0);
  const [heightM, setHeightM] = useState(2.6);
  const [speakerFrontM, setSpeakerFrontM] = useState(1.0);
  const [speakerSideM, setSpeakerSideM] = useState(0.8);
  const [listenerFrontM, setListenerFrontM] = useState(1.9);

  const applyRoom = (nextLength: number, nextWidth: number) => {
    const { room, contents } = resizeRoom(
      { lengthM, widthM },
      { lengthM: nextLength, widthM: nextWidth },
      {
        spk: { xM: speakerSideM, yM: speakerFrontM },
        lis: { xM: widthM / 2, yM: listenerFrontM },
      },
      { bounds: PLAN_BOUNDS },
    );
    setLengthM(room.lengthM);
    setWidthM(room.widthM);
    setSpeakerSideM(contents.spk.xM);
    setSpeakerFrontM(contents.spk.yM);
    setListenerFrontM(contents.lis.yM);
  };

  const reflections = useMemo(
    () =>
      calculateFirstReflections(
        lengthM,
        widthM,
        heightM,
        speakerFrontM,
        speakerSideM,
        listenerFrontM,
      ),
    [lengthM, widthM, heightM, speakerFrontM, speakerSideM, listenerFrontM],
  );

  const lisX = widthM / 2;
  const leftY = reflections.leftWallDistFromFrontM;
  const rightY = reflections.rightWallDistFromFrontM;
  const ceilY = reflections.ceilingDistFromFrontM;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pb-6 border-b border-[var(--app-border)]">
        <ToolDimSlider label="Room length" valueM={lengthM} minM={3} maxM={10} stepM={0.1} unit={unit} onChangeM={(v) => applyRoom(v, widthM)} testId="dim-length" />
        <ToolDimSlider label="Room width" valueM={widthM} minM={2.5} maxM={8} stepM={0.1} unit={unit} onChangeM={(v) => applyRoom(lengthM, v)} testId="dim-width" />
        <ToolDimSlider
          label="Listener from front"
          valueM={listenerFrontM}
          minM={1}
          maxM={Math.max(1.1, lengthM - 0.5)}
          stepM={0.1}
          unit={unit}
          onChangeM={setListenerFrontM}
        />
        <ToolDimSlider
          label="Speaker from front"
          valueM={speakerFrontM}
          minM={0.4}
          maxM={Math.max(0.5, lengthM - 0.5)}
          stepM={0.1}
          unit={unit}
          onChangeM={setSpeakerFrontM}
        />
        <ToolDimSlider
          label="Speaker from left"
          valueM={speakerSideM}
          minM={0.3}
          maxM={Math.max(0.35, widthM / 2)}
          stepM={0.1}
          unit={unit}
          onChangeM={setSpeakerSideM}
        />
        <ToolDimSlider label="Room height" valueM={heightM} minM={2} maxM={4} stepM={0.1} unit={unit} onChangeM={setHeightM} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RoomPlan2D
          lengthM={lengthM}
          widthM={widthM}
          unit={unit}
          resizeWalls
          roomBounds={PLAN_BOUNDS}
          onRoomSizeChange={applyRoom}
          markers={[
            { id: 'spk', xM: speakerSideM, yM: speakerFrontM, label: 'S', color: 'var(--rt-accent)' },
            { id: 'lis', xM: lisX, yM: listenerFrontM, label: 'L', color: 'var(--rt-accent)' },
          ]}
          onMarkerMove={(id, x, y) => {
            if (id === 'spk') {
              setSpeakerSideM(x);
              setSpeakerFrontM(y);
            } else if (id === 'lis') {
              setListenerFrontM(y);
            }
          }}
          rays={[
            {
              id: 'to-left',
              x1M: speakerSideM,
              y1M: speakerFrontM,
              x2M: 0,
              y2M: leftY,
            },
            {
              id: 'from-left',
              x1M: 0,
              y1M: leftY,
              x2M: lisX,
              y2M: listenerFrontM,
            },
            {
              id: 'to-right',
              x1M: speakerSideM,
              y1M: speakerFrontM,
              x2M: widthM,
              y2M: rightY,
            },
            {
              id: 'from-right',
              x1M: widthM,
              y1M: rightY,
              x2M: lisX,
              y2M: listenerFrontM,
            },
          ]}
          points={[
            { id: 'ml', xM: 0, yM: leftY, label: 'M', color: 'var(--rt-accent)' },
            { id: 'mr', xM: widthM, yM: rightY, label: 'M', color: 'var(--rt-accent)' },
            { id: 'cloud', xM: lisX, yM: ceilY, label: 'C', color: 'color-mix(in srgb, var(--rt-accent) 60%, transparent)' },
          ]}
          dims={[
            {
              id: 'mirror-y',
              x1M: 0,
              y1M: 0,
              x2M: 0,
              y2M: leftY,
              label: formatLength(leftY, unit),
              offsetPx: -16,
            },
          ]}
          testId="first-reflection-floorplan"
        />

        <div className="grid grid-cols-1 gap-4 content-start">
          <ToolMetricCard
            label="Side wall reflection"
            value={formatLength(reflections.leftWallDistFromFrontM, unit)}
            hint="from front wall along side wall"
            emphasized
            testId="metric-side-wall"
          />
          <ToolMetricCard
            label="Ceiling cloud center"
            value={formatLength(reflections.ceilingDistFromFrontM, unit)}
            hint="from front wall (plan marker C)"
            testId="metric-ceiling-cloud"
          />
          <ToolMetricCard label="Recommended panel" value="4 in / 10 cm" hint="porous absorber with air gap" />
        </div>
      </div>

      <ToolLeadIn
        result={`Side-wall reflection point at ${formatLength(reflections.leftWallDistFromFrontM, unit)} from the front wall — that's where your first panel goes.`}
        ctaLabel="Open 3D raytrace solver"
        seed={{ w: widthM, l: lengthM, h: heightM, units: 'm' }}
      />
    </div>
  );
}
